/**
 * Loan Calculator Composable
 * Implements the Standard Amortization Formula for installment loans
 */

/**
 * Calculate loan details using the Standard Amortization Formula
 * 
 * Monthly Payment Formula: M = P * [r(1+r)^n] / [(1+r)^n - 1]
 * Where:
 *   M = Monthly payment
 *   P = Principal (original loan amount)
 *   r = Monthly interest rate (annual rate / 12)
 *   n = Number of payments (term in months)
 * 
 * @param {number} principal - The original loan amount
 * @param {number} annualRate - Annual interest rate as a percentage (e.g., 5.5 for 5.5%)
 * @param {number} months - Total loan term in months
 * @param {string|Date} startDate - The loan start date
 * @returns {Object} Loan details including monthlyPayment, currentBalance, payoffDate, totalInterest, progress
 */
export function calculateLoanDetails(principal, annualRate, months, startDate) {
  // Validate inputs
  if (!principal || principal <= 0) {
    return {
      monthlyPayment: 0,
      currentBalance: 0,
      payoffDate: null,
      totalInterest: 0,
      progress: 0
    };
  }

  if (!months || months <= 0) {
    return {
      monthlyPayment: 0,
      currentBalance: principal,
      payoffDate: null,
      totalInterest: 0,
      progress: 0
    };
  }

  // Convert annual rate to monthly rate (as decimal)
  const monthlyRate = (annualRate || 0) / 100 / 12;
  
  let monthlyPayment;
  
  if (monthlyRate === 0) {
    // No interest - simple division
    monthlyPayment = principal / months;
  } else {
    // Standard Amortization Formula: M = P * [r(1+r)^n] / [(1+r)^n - 1]
    const compoundFactor = Math.pow(1 + monthlyRate, months);
    monthlyPayment = principal * (monthlyRate * compoundFactor) / (compoundFactor - 1);
  }

  // Calculate total amount to be paid and total interest
  const totalPayment = monthlyPayment * months;
  const totalInterest = totalPayment - principal;

  // Parse start date
  const start = startDate ? new Date(startDate) : new Date();
  
  // Calculate payoff date (add months to start date)
  const payoffDate = new Date(start);
  payoffDate.setMonth(payoffDate.getMonth() + months);

  // Calculate current balance and progress
  const today = new Date();
  let currentBalance = principal;
  let progress = 0;

  if (today >= start) {
    // Calculate how many months have passed since the loan started
    const monthsElapsed = calculateMonthsElapsed(start, today);
    const paymentsMade = Math.min(monthsElapsed, months);

    if (paymentsMade >= months) {
      // Loan is fully paid off
      currentBalance = 0;
      progress = 100;
    } else if (paymentsMade > 0) {
      if (monthlyRate === 0) {
        // No interest - simple calculation
        currentBalance = principal - (monthlyPayment * paymentsMade);
      } else {
        // Calculate remaining balance using amortization formula
        // B = P * [(1+r)^n - (1+r)^p] / [(1+r)^n - 1]
        // Where p = number of payments made
        const compoundFactor = Math.pow(1 + monthlyRate, months);
        const paymentsFactor = Math.pow(1 + monthlyRate, paymentsMade);
        currentBalance = principal * (compoundFactor - paymentsFactor) / (compoundFactor - 1);
      }
      
      // Calculate progress as percentage of principal paid off
      const principalPaid = principal - currentBalance;
      progress = (principalPaid / principal) * 100;
    }
  }

  // Ensure values are not negative due to floating point errors
  currentBalance = Math.max(0, currentBalance);
  progress = Math.min(100, Math.max(0, progress));

  return {
    monthlyPayment: roundToTwoDecimals(monthlyPayment),
    currentBalance: roundToTwoDecimals(currentBalance),
    payoffDate,
    totalInterest: roundToTwoDecimals(totalInterest),
    progress: roundToTwoDecimals(progress)
  };
}

/**
 * Calculate the number of full months elapsed between two dates
 * @param {Date} startDate - Start date
 * @param {Date} endDate - End date
 * @returns {number} Number of full months elapsed
 */
function calculateMonthsElapsed(startDate, endDate) {
  const start = new Date(startDate);
  const end = new Date(endDate);
  
  let months = (end.getFullYear() - start.getFullYear()) * 12;
  months += end.getMonth() - start.getMonth();
  
  // Only count full months (if we haven't reached the day of month yet, subtract 1)
  if (end.getDate() < start.getDate()) {
    months--;
  }
  
  return Math.max(0, months);
}

/**
 * Round a number to two decimal places
 * @param {number} num - Number to round
 * @returns {number} Rounded number
 */
function roundToTwoDecimals(num) {
  return Math.round((num + Number.EPSILON) * 100) / 100;
}

/**
 * Currency symbols map
 */
const currencySymbols = {
  'USD': '$',
  'EUR': '€',
  'GBP': '£',
  'JPY': '¥',
  'TRY': '₺',
  'TL': '₺',  // Alias for backwards compatibility
  'CAD': 'C$',
  'AUD': 'A$',
  'CHF': 'Fr'
};

/**
 * Get currency symbol
 * @param {string} currency - Currency code
 * @returns {string} Currency symbol
 */
export function getCurrencySymbol(currency = 'USD') {
  return currencySymbols[currency] || '$';
}

/**
 * Format currency for display
 * @param {number} amount - Amount to format
 * @param {string} currency - Currency code (USD, EUR, GBP, etc.)
 * @returns {string} Formatted currency string
 */
export function formatCurrency(amount, currency = 'USD') {
  const symbol = getCurrencySymbol(currency);
  return `${symbol}${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/**
 * Format date for display
 * @param {Date} date - Date to format
 * @returns {string} Formatted date string (MMM YYYY)
 */
export function formatPayoffDate(date) {
  if (!date) return 'N/A';
  return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
}

/**
 * Installment loan types that require amortization fields
 */
export const INSTALLMENT_LOAN_TYPES = ['MORTGAGE', 'CAR_LOAN', 'PERSONAL_LOAN'];

/**
 * Simple liability types that just need current balance
 */
export const SIMPLE_LIABILITY_TYPES = ['CREDIT_CARD', 'OTHER', 'TAX'];

/**
 * Determine if a liability type is an installment loan
 * @param {string} type - Liability type
 * @returns {boolean} True if it's an installment loan
 */
export function isInstallmentLoan(type) {
  return INSTALLMENT_LOAN_TYPES.includes(type?.toUpperCase());
}

/**
 * Determine if a liability type is a simple type (just current balance)
 * @param {string} type - Liability type
 * @returns {boolean} True if it's a simple liability type
 */
export function isSimpleLiability(type) {
  return SIMPLE_LIABILITY_TYPES.includes(type?.toUpperCase());
}

/**
 * Get liability type display name
 * @param {string} type - Liability type code
 * @returns {string} Human-readable type name
 */
export function getLiabilityTypeName(type) {
  const typeNames = {
    'CREDIT_CARD': 'Credit Card',
    'MORTGAGE': 'Mortgage',
    'CAR_LOAN': 'Car Loan',
    'PERSONAL_LOAN': 'Personal Loan',
    'TAX': 'Tax',
    'OTHER': 'Other'
  };
  return typeNames[type?.toUpperCase()] || type || 'Unknown';
}

/**
 * Get liability type icon
 * @param {string} type - Liability type code
 * @returns {string} Emoji icon for the type
 */
export function getLiabilityTypeIcon(type) {
  const icons = {
    'CREDIT_CARD': '💳',
    'MORTGAGE': '🏠',
    'CAR_LOAN': '🚗',
    'PERSONAL_LOAN': '💰',
    'TAX': '🏛️',
    'OTHER': '📋'
  };
  return icons[type?.toUpperCase()] || '📋';
}

export default function useLoanCalculator() {
  return {
    calculateLoanDetails,
    formatCurrency,
    getCurrencySymbol,
    formatPayoffDate,
    isInstallmentLoan,
    isSimpleLiability,
    getLiabilityTypeName,
    getLiabilityTypeIcon,
    INSTALLMENT_LOAN_TYPES,
    SIMPLE_LIABILITY_TYPES
  };
}

